import { Component, OnInit, Inject } from '@angular/core';
import { AccountService } from '../services/account.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-storage',
  templateUrl: './storage.component.html',
  styleUrls: ['./storage.component.scss']
})
export class AccountSettingsStorageComponent implements OnInit {
  storageData: any = null;
  loading: boolean = true;
  error: string | null = null;

  constructor(
    @Inject('accountInformation') public accountInformation: any,
    @Inject('canAdminister') private canAdminister: boolean,
    private accountService: AccountService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadStorageData();
  }

  getAccountDisplayName(): string {
    // Try multiple sources for account information
    if (this.storageData?.accountId) {
      return this.storageData.accountId.toString();
    }
    
    if (this.accountInformation?.general?.external_id) {
      return this.accountInformation.general.external_id.toString();
    }
    
    if (this.accountInformation?.general?.name) {
      return this.accountInformation.general.name;
    }
    
    if (this.accountInformation?.general?.id) {
      return this.accountInformation.general.id.toString();
    }
    
    console.log('Account information available:', this.accountInformation);
    console.log('Storage data available:', this.storageData);
    
    return this.translate.instant('STORAGE.UNKNOWN');
  }

  async loadStorageData() {
    try {
      this.loading = true;
      this.error = null;
      
      const accountId = this.accountInformation?.general?.external_id;
      if (!accountId) {
        throw new Error('Account ID not found');
      }
      
      let storageDataResponse = await this.accountService.getAccountStorageUsage(accountId);
      this.storageData = storageDataResponse.data;
      // Ensure data integrity
      if (this.storageData) {
        // Ensure totalSize is properly calculated from breakdown if missing
        if (!this.storageData.totalSize && this.storageData.breakdown) {
          this.storageData.totalSize = Object.values(this.storageData.breakdown)
            .reduce((sum: number, category: any) => sum + (category.size || 0), 0);
        }
        
        // Ensure totalFormatted is set if missing
        if (!this.storageData.totalFormatted && this.storageData.totalSize) {
          this.storageData.totalFormatted = this.formatBytes(this.storageData.totalSize);
        }
      }
    } catch (error) {
      console.error('Error loading storage data:', error);
      this.error = await this.translate.get('STORAGE.ERROR_LOADING_DATA').toPromise();
    } finally {
      this.loading = false;
    }
  }

  async loadDetailedAnalysis() {
    try {
      this.loading = true;
      this.error = null;
      
      const accountId = this.accountInformation?.general?.external_id;
      if (!accountId) {
        throw new Error('Account ID not found');
      }

      this.storageData = await this.accountService.getAccountStorageAnalysis(accountId);
    } catch (error) {
      console.error('Error loading detailed storage analysis:', error);
      this.error = await this.translate.get('STORAGE.ERROR_LOADING_DETAILED_ANALYSIS').toPromise();
    } finally {
      this.loading = false;
    }
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatCategoryName(key: string): string {
    // Use translation keys for category names
    const categoryMap: { [key: string]: string } = {
      'firingHistory': 'STORAGE.FIRING_HISTORY',
      'maintenanceNotes': 'STORAGE.MAINTENANCE_NOTES',
      'alerts': 'STORAGE.ALERTS',
      'diagnostics': 'STORAGE.DIAGNOSTICS'
    };
    
    const translationKey = categoryMap[key];
    if (translationKey) {
      return this.translate.instant(translationKey);
    }
    
    // Fallback for unknown categories
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  }

  getPercentageUsed(totalSize: number, breakdown: any): number {
    if (!totalSize || totalSize === 0) return 0;
    // Use a more realistic storage limit or calculate from breakdown
    const storageLimit = 1000000000; // 1GB limit
    const percentage = Math.round((totalSize / storageLimit) * 100);
    return percentage;
  }
}
